import { cn } from '~/common/utils/styleUtil';
import { AddToCart } from '~/features/catalog/components/AddToCart';
import { ArticleSellUnit } from '~/features/catalog/components/article/ArticleSellUnit';
import { ArticlePrice } from '~/features/catalog/components/article/ArticlePrice';
import { ArticleSecondaryText } from '~/features/catalog/components/article/ArticleSecondaryText';
import { useArticleDetailPath } from '~/features/catalog/hooks/useArticleDetailPath';
import type { BghArticle } from '~/common/types/bghArticle';
import { useCatalogBghArticle } from '~/features/catalog/hooks/useCatalogBghArticle';
import { TgsArticleImage } from '~/common/components/ArticleImage';
import { Link } from 'react-router';
import { CatalogArticleNotAvailable } from '~/features/catalog/components/CatalogArticleNotAvailable';
import { useScreenSize } from '~/common/hooks/useScreenSize';
import ArticleComparisonPrice from '~/features/catalog/components/article/ArticleComparisonPrice.tsx';
import ArticleInfoIcons from '~/features/catalog/components/ArticleInfoIcons.tsx';
import React from 'react';

type CatalogListItemProps = {
  index: number;
  article: BghArticle;
  isAuthenticated: boolean;
  className?: string;
};

export function CatalogBghListItem({ article, isAuthenticated, className }: Readonly<CatalogListItemProps>) {
  const catalogArticle = useCatalogBghArticle(article);
  const articleDetailPath = useArticleDetailPath(catalogArticle.articleNumber);
  const { isXlDown } = useScreenSize();

  const handleInteractiveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <article
      className={cn(
        'group border-b desktop:border-b-0 border-r-0 p-4 xl:pl-4 hover:bg-neutral-50 h-full flex flex-col',
        className
      )}
    >
      <Link
        to={articleDetailPath}
        state={{ quantity: catalogArticle.quantity }}
        id={catalogArticle.articleNumber}
        className="flex flex-row flex-nowrap gap-4 items-stretch h-full no-underline text-inherit"
        onClick={e => {
          const target = e.target as HTMLElement;
          if (target.closest('.interactive-element')) {
            e.preventDefault();
          }
        }}
      >
        <div className="flex">
          <TgsArticleImage
            celumId={catalogArticle.celumId}
            articleNumber={catalogArticle.articleNumber}
            alt={catalogArticle.description}
            size={isXlDown ? 'small' : 'normal'}
          />
        </div>

        <div className="grow min-w-0">
          <div className="flex flex-col justify-center h-full">
            <div
              className={cn(
                'text-base font-bold line-clamp-2',
                catalogArticle.isAction && !catalogArticle.isHit && 'text-primary'
              )}
              data-testid={`article-list-description-${article.articleNumber}`}
            >
              {catalogArticle.description}
            </div>
            <ArticleSecondaryText
              articleNumber={catalogArticle.articleNumber}
              rohstoffHerkunft={catalogArticle.rohstoffHerkunft}
              brand={catalogArticle.brand}
              lineClamp={3}
            />
            <ArticleInfoIcons article={catalogArticle} className="flex py-1" isAuthenticated={isAuthenticated} />
          </div>
        </div>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div className="grow-0 shrink-0 interactive-element cursor-default" onClick={handleInteractiveClick}>
          <div className="flex flex-row items-center h-full">
            <ArticlePrice
              price={catalogArticle.price}
              oldPrice={catalogArticle.oldPrice}
              isAction={catalogArticle.isAction}
              actionValidTo={catalogArticle.actionValidTo}
              unitText={catalogArticle.unitText}
              className="lg:mx-4 xl:mx-8"
              minimize={true}
              isHit={catalogArticle.isHit}
            />
            <div>
              {!catalogArticle.isAction && (
                <ArticleComparisonPrice
                  hasComparisonPrice={catalogArticle.hasComparisonPrice}
                  comparisonPrice={catalogArticle.comparisonPrice}
                  comparisonPriceUnitText={catalogArticle.comparisonPriceUnitText}
                />
              )}
              <ArticleSellUnit
                selectedArticle={catalogArticle}
                allArticles={catalogArticle.allArticles}
                onArticleChange={changeInfo =>
                  catalogArticle.setSelectedArticleNumber(changeInfo.articleNumber.toString())
                }
              />
              <div className="flex">
                <AddToCart
                  articleNumber={catalogArticle.articleNumber}
                  articleDescriptionText={catalogArticle.description}
                  mainArticleId={catalogArticle.mainArticleId}
                  isExternalArticle={false}
                  isProfitPlus={catalogArticle.isProfitPlus}
                  calculatedTotalPriceText={catalogArticle.calculatedTotalPriceText}
                  quantity={catalogArticle.quantity}
                  onQuantityChange={catalogArticle.setQuantity}
                  isAuthenticated={isAuthenticated}
                  hideCart={catalogArticle.hideCart}
                  className="min-w-[200px]"
                  writeArticleQuantityCache={true}
                  loadArticleQuantityCache={true}
                  hideAddToTemplate={catalogArticle.isInactive}
                />
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Fehlermeldungen außerhalb des Links */}
      {(catalogArticle.hideCart || catalogArticle.showSellOffMessage) && (
        <CatalogArticleNotAvailable
          showSellOffMessage={catalogArticle.showSellOffMessage}
          showCurrentlyNotAvailableMessage={catalogArticle.showCurrentlyNotAvailableMessage}
          substituteArticleNumber={catalogArticle.substituteArticleNumber}
          substituteArticleText={catalogArticle.substituteArticleText}
          notInWebshop={catalogArticle.notInWebshop}
          className={catalogArticle.showSellOffMessage ? 'mt-2' : 'mt-0'}
        />
      )}
    </article>
  );
}
